/*
 * Shared bits for the authenticator-enrollment surfaces.
 */
import { getUserRole } from "../../utils/helpers";

// Only ADMIN and SUPERADMIN may mint an enrollment code for another user. The
// server enforces this too and returns 403 — but the button is hidden rather
// than shown-and-erroring, so a 403 reaching the client means the two
// disagree, which is a bug worth seeing rather than a message worth rendering.
const INVITE_ROLES = ["ADMIN", "SUPERADMIN"];
export const canInviteOthers = () =>
  INVITE_ROLES.includes(String(getUserRole() || "").toUpperCase());

// Display names for the provider values GetEnrollmentSummary / GetMFAProvider
// may report. Unknown values are humanised rather than dropped, so a provider
// added server-side still reads sensibly without a frontend release.
const PROVIDER_NAMES = {
  authnull: "AuthNull Authenticator",
  fcm: "AuthNull Authenticator",
  expo: "AuthNull Authenticator",
  okta: "Okta",
  duo: "Duo",
  azure_ad: "Microsoft Entra ID",
};

export const providerName = (p) => {
  if (!p) return "AuthNull Authenticator";
  return PROVIDER_NAMES[p] || p.replace(/_/g, " ");
};

// GetEnrollmentSummary accepts 1–90 and echoes the window back as `windowDays`.
export const DAY_WINDOWS = [1, 7, 30, 90];
export const DEFAULT_DAYS = 7;

// ── Expiry ────────────────────────────────────────────────────────────────
// Coarse countdown — a 7-day token needs no seconds display until it is nearly
// gone. Accepts an ISO string or a Date.
export const countdown = (when) => {
  const ms = new Date(when).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  if (ms <= 0) return "expired";
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
};

export const fmtExpiry = (when) => {
  const d = new Date(when);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

// ── The compact invite payload (`?d=…` on /enroll) ─────────────────────────
// Unpadded base64url JSON. Decoded on the public page with no network call so
// the recipient sees who the invite is for even offline.
//
//   n  orgName · e  email · x  expiry (unix SECONDS) · o  orgId
//   tk token   · k  kind  · b  API base
//
// Returns null on anything malformed — a bad link must render an explanation,
// never a half-populated page.
export const decodeInvitePayload = (d) => {
  if (!d || typeof d !== "string") return null;
  try {
    const b64 = d.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    // TextDecoder rather than escape/unescape so non-ASCII org names survive.
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

// `x` is unix seconds; JS wants milliseconds.
export const expiryFromPayload = (p) =>
  typeof p?.x === "number" ? new Date(p.x * 1000) : null;

// The custom scheme belongs on a button press and as QR content — never as a
// rendered link. A mail client will not linkify an unknown scheme, and in a
// desktop chat it is inert text that looks broken.
export const deepLinkFor = (d) => `authnull://enroll?v=2&d=${d}`;
